import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { productImageUrl } from '~Utils/images';

/** At most this many tiles; the last one carries a "+N" for the rest. */
const MAX_TILES = 4;

/**
 * One photo fills the circle; two sit side by side; three put one on the left
 * and two stacked on the right; four (or more, with a "+N" on the last) make a
 * 2x2 grid. Laid out as a grid so the tiles always tile the circle exactly.
 */
const Grid = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 2px;
  background: var(--eposSurface);

  &.tiles-1 {
    grid-template: 1fr / 1fr;
  }
  &.tiles-2 {
    grid-template: 1fr / 1fr 1fr;
  }
  &.tiles-3 {
    grid-template: 1fr 1fr / 1fr 1fr;
    > :first-of-type {
      grid-row: 1 / span 2;
    }
  }
  &.tiles-4 {
    grid-template: 1fr 1fr / 1fr 1fr;
  }
`;

const Tile = styled.div<{ src: string }>`
  position: relative;
  min-width: 0;
  min-height: 0;
  background: var(--eposCategoryBackground) url('${({ src }) => src}') center / cover no-repeat;
`;

/** "+N" over the last tile when there are more picks than tiles. */
const More = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: var(--colorWhite);
  font-size: 14px;
  font-weight: 800;
`;

interface Props {
  imageUrls: string[];
  /** Pixel size the collage is drawn at - each tile asks the CDN for about this. */
  sizePx: number;
}

const ChipCollage: FC<Props> = memo(({ imageUrls, sizePx }) => {
  const shown = imageUrls.slice(0, MAX_TILES);
  const hidden = imageUrls.length - shown.length;

  return (
    <Grid className={`tiles-${shown.length}`} data-testid="epos-chip-collage">
      {shown.map((url, index) => (
        <Tile key={`${url}-${index}`} src={productImageUrl(url, sizePx)}>
          {hidden > 0 && index === shown.length - 1 && <More>+{hidden}</More>}
        </Tile>
      ))}
    </Grid>
  );
});

ChipCollage.displayName = 'ChipCollage';

export default ChipCollage;
